import Axios, { AxiosError } from 'axios';
import React, { SyntheticEvent, useEffect, useRef, useContext, useCallback, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import validate from 'validate.js';
import { provideEmailConstraints, providePhoneConstraints } from '../../constraints/ProvidePhone';
import { useMergedState } from '../../hooks/useMergedState';
import { IBaseForm, IBaseState } from '../../types/types';
import Alert, { AlertType } from '../components/Alert';
import AuthLogo from '../components/AuthLogo';
import { UseTranslate } from '../../hooks/Translate';
import AppContext from '../../contexts/AppContext';
import checkSession from '../../utils/checkSession';
import { isOtpLimitError, otpLimitText } from '../../utils/otpLimit';

type CheckPhoneState = Partial<IBaseState & IBaseForm>;
enum UserType {
  COURIER = 4
}

export const CheckPhone = () => {

  const { setReturnUrl } = useContext(AppContext);
  const location = useLocation();
  const firstLoad = useRef(true);

  const [userType, setUserType] = useState<number>();
  const setParamData = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (firstLoad.current) {
      setReturnUrl(params.get("next"));
    }
    firstLoad.current = false;
    setUserType(+(params.get("userType") || 0));
  }, [setReturnUrl, location])


  useEffect(() => {
    try {
      checkSession();
    } catch (error) {

    }
  }, []);

  useEffect(() => {
    setParamData();
  }, [setParamData]);

  const [state, setState] = useMergedState<CheckPhoneState>({
    errors: {},
    validated: false,
    alertProps: {
      type: AlertType.NONE,
    },
  });

  const navigate = useNavigate();
  const [translate] = UseTranslate();

  const validateOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let errors;
    if (userType === UserType.COURIER) {
      errors = validate(
        { phone: event.currentTarget.value },
        providePhoneConstraints(translate('cas.recovery.phoneStart'), translate('cas.recovery.phoneLength'), translate('cas.recovery.blankPhone')),
        { fullMessages: false }
      );
    } else {
      errors = validate(
        { email: event.currentTarget.value },
        provideEmailConstraints(translate('cas.recovery.blankEmail'), translate('cas.recovery.emailFormat')),
        { fullMessages: false }
      );
    }
    if (errors) {
      for (const key in errors) {
        errors[key] = errors[key][0];
      }

      setState({ errors });
    } else {
      setState({ errors: {} });
    }
  };

  const validateOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (userType === UserType.COURIER) {
      if (['+', '-', '.', 'e'].indexOf(event.key) !== -1) {
        event.preventDefault();
      }
    }

  };


  const onFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const values = Object.fromEntries<any>(fd.entries());
    let errors
    if (userType === UserType.COURIER) {
      errors = validate(values, providePhoneConstraints(translate('cas.recovery.phoneStart'), translate('cas.recovery.phoneLength'), translate('cas.recovery.blankPhone')), { fullMessages: false });
    } else {
      errors = validate(values, provideEmailConstraints(translate('cas.recovery.blankEmail'), translate('cas.recovery.emailFormat')), { fullMessages: false });

    }

    if (errors) {
      for (const key in errors) {
        errors[key] = errors[key][0];
      }

      setState({ errors });
      setState({
        alertProps: {
          type: AlertType.ERROR,
          title: translate('cas.registration.registerError'),
          message: translate('cas.registration.registerCheckErrorFields'),
        },
      });
    } else {
      setState({ errors: {} });
      setState({ alertProps: { type: AlertType.NONE } });
      requestPasswordRecovery(values);
    }

    setState({ validated: true });
  };

  const requestPasswordRecovery = async (data: Record<string, unknown>) => {
    try {
      const type = userType === UserType.COURIER ? 'phone' : 'email';
      const value = userType === UserType.COURIER ? data.phone : data.email;
      const url = `${process.env.REACT_APP_CAS_API}/users/initiatePasswordRecovery/${value}/${type}`;
      const userData = await Axios.post(url);
      const { _id } = userData.data.data;
      let path = `/verify/contact/${_id}`;
      navigate(path);
    } catch (e) {
      let message;
      let remainingSec;
      if (e instanceof AxiosError && e.response) {
        message = e.response.data.message
        remainingSec = e.response.data.data?.remainingSec
      }
      if (message === 'USER_NOT_FOUND') {
        message = translate('cas.registration.user_not_found');
      } else if (isOtpLimitError(message)) {
        message = otpLimitText(translate, message, remainingSec);
      }
      setState({
        alertProps: {
          type: AlertType.ERROR,
          title: translate('cas.registration.errorNotice'),
          message,
        },
      });
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="text-center">
        <AuthLogo />
      </div>

      <div className="mb-4">
        <div className="small">
          <h6>{translate('cas.registration.resetPswd')}</h6>
        </div>
      </div>

      <Alert
        type={state.alertProps!.type}
        title={state.alertProps!.title}
        message={state.alertProps!.message}
      />
      <Form validated={state.validated} onSubmit={onFormSubmit} noValidate>
        {userType === UserType.COURIER ? <>
          <Form.Group className='mb-3' controlId="phone">
            <Form.Label className="small phone-icon">
              {userType === UserType.COURIER ? translate('cas.recovery.typePhoneNumber') : translate('cas.recovery.email')}
            </Form.Label>
            <Form.Control
              autoComplete="off"
              type="number"
              placeholder={translate('cas.recovery.phonePlaceholder')}
              name="phone"
              isInvalid={'phone' in state.errors!}
              required
              onChange={validateOnChange}
              onKeyDown={validateOnKeyDown}
            />
            <Form.Control.Feedback type="invalid">
              {state.errors?.phone}
            </Form.Control.Feedback>
          </Form.Group>
        </> : <>
          <Form.Group className='mb-3' controlId="email">
            <Form.Label className="small email-icon">
              {translate('cas.recovery.email')}
            </Form.Label>
            <Form.Control
              autoComplete="off"
              type="string"
              placeholder={translate('cas.recovery.emailPlaceholder')}
              name="email"
              isInvalid={'email' in state.errors!}
              required
              onChange={validateOnChange}
              onKeyDown={validateOnKeyDown}
            />
            <Form.Control.Feedback type="invalid">
              {state.errors?.email}
            </Form.Control.Feedback>
          </Form.Group>
        </>}


        <Button variant="primary" type="submit" className="w-100">
          {translate('cas.registration.next')}
        </Button>
      </Form>
    </div>
  );
};
